/** Billing (tenant admin) — current plan, seat-band pricing grid, and
 * Razorpay checkout. Flow: create order server-side → open the Razorpay
 * window (UPI / cards) → the SERVER verifies the returned signature —
 * nothing activates on the browser's word alone. The webhook is the
 * belt-and-braces path when the browser callback is lost.
 *
 * All prices come from the backend plan catalog — nothing is hard-coded
 * here.
 */
import { useEffect, useState } from "react";
import { ApiError } from "../../api/http";
import * as api from "../../api/billing";
import { SUPPORT_EMAIL } from "../../config/brand";
import { Badge, Button, Card, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { CheckoutDismissed, openRazorpayCheckout } from "../../utils/razorpay";

export function formatMinor(minor: number, currency: string): string {
  const value = minor / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

export function BillingPage() {
  const t = useT();
  const { push } = useToast();
  const [billing, setBilling] = useState<api.MyBilling | null>(null);
  const [plans, setPlans] = useState<api.PublicPlans | null>(null);
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  const load = () => {
    api.myBilling().then(setBilling).catch(() => setBilling(null));
    api.publicPlans().then(setPlans).catch(() => setPlans(null));
  };

  useEffect(() => {
    let cancelled = false;
    api.myBilling().then((b) => !cancelled && setBilling(b)).catch(() => undefined);
    api.publicPlans().then((p) => !cancelled && setPlans(p)).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const onBuy = async (plan: api.PlanOut) => {
    setBusyPlan(plan.code);
    try {
      const session = await api.createCheckout(plan.code, period);
      const success = await openRazorpayCheckout({
        keyId: session.key_id,
        amountMinor: session.amount_minor,
        currency: session.currency,
        razorpayOrderId: session.razorpay_order_id,
        name: session.name,
        description: session.description,
      });
      await api.verifyPayment(
        session.order_id,
        success.razorpay_payment_id,
        success.razorpay_signature,
      );
      push("success", t("billing.paid"));
      load();
    } catch (e) {
      if (e instanceof CheckoutDismissed) {
        push("info", t("billing.cancelled"));
      } else {
        push("error", e instanceof ApiError ? e.message : t("common.error.generic"));
      }
    } finally {
      setBusyPlan(null);
    }
  };

  if (!billing || !plans) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("billing.title")}</h1>
        <p className="mt-1 text-muted">{t("billing.subtitle")}</p>
      </div>

      {!billing.configured && (
        <p role="status" className="rounded-control bg-canvas p-3 text-sm text-muted">
          {t("billing.notConfigured")}
        </p>
      )}

      <Card title={t("billing.currentPlan")}>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-2xl font-extrabold text-ink">
              {billing.plan_name ?? t("billing.trial")}
            </p>
            <p className="text-sm text-muted">
              {t("billing.seats")}: {billing.seats_active} / {billing.seats_limit ?? "∞"}
            </p>
          </div>
          {billing.expires_at && (
            <div>
              <p className="text-xs uppercase text-muted">{t("billing.renewsUntil")}</p>
              <p className="font-semibold text-ink">
                {new Date(billing.expires_at).toLocaleDateString()}
                {billing.days_left !== null && (
                  <span className={`ml-2 text-sm ${billing.days_left <= 7 ? "text-danger" : "text-muted"}`}>
                    ({billing.days_left} {t("billing.daysLeft")})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-ink">{t("billing.plans")}</h2>
        <div className="flex gap-2">
          {(["monthly", "yearly"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-control border px-3 py-1.5 text-sm font-semibold ${period === p ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted hover:text-ink"}`}
            >
              {t(p === "monthly" ? "billing.monthly" : "billing.yearly")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {plans.plans.map((plan) => {
          const amount = period === "yearly" ? plan.yearly_price_minor : plan.monthly_price_minor;
          const current = billing.plan_code === plan.code;
          return (
            <Card key={plan.code} className={current ? "border-brand" : ""}>
              <div className="flex items-baseline justify-between">
                <h3 className="text-lg font-bold text-ink">{plan.name}</h3>
                {current && <Badge tone="brand">{t("billing.current")}</Badge>}
              </div>
              <p className="text-sm text-muted">{plan.seats_label}</p>
              <p className="mt-3 text-2xl font-extrabold text-ink">
                {plan.purchasable
                  ? formatMinor(amount, plans.currency)
                  : t("billing.contactUs")}
                {plan.purchasable && (
                  <span className="text-sm font-normal text-muted">
                    {" "}/ {t(period === "monthly" ? "billing.month" : "billing.year")}
                  </span>
                )}
              </p>
              <p className="mt-2 min-h-10 text-sm text-muted">{plan.description}</p>
              {plan.purchasable ? (
                <Button
                  className="mt-3 w-full"
                  loading={busyPlan === plan.code}
                  disabled={busyPlan !== null || !billing.configured}
                  onClick={() => onBuy(plan)}
                >
                  {current ? t("billing.renew") : t("billing.choose")}
                </Button>
              ) : (
                <a
                  className="mt-3 block text-center text-sm font-semibold text-brand hover:underline"
                  href={`mailto:${SUPPORT_EMAIL}`}
                >
                  {t("billing.contactUs")} →
                </a>
              )}
            </Card>
          );
        })}
      </div>

      {billing.orders.length > 0 && (
        <Card title={t("billing.orders")}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted">
                <th className="py-1">{t("billing.date")}</th>
                <th>{t("billing.plan")}</th>
                <th>{t("billing.amount")}</th>
                <th>{t("billing.status")}</th>
              </tr>
            </thead>
            <tbody>
              {billing.orders.map((order) => (
                <tr key={order.id} className="border-t border-line">
                  <td className="py-2">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    {order.plan_code} · {order.period}
                  </td>
                  <td>{formatMinor(order.amount_minor, order.currency)}</td>
                  <td>
                    <Badge tone={order.status === "paid" ? "success" : "neutral"}>
                      {order.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
